// ─── Module-level cache ───────────────────────────────────────────────────────
// localStorage is parsed ONCE per session on first access.
// All subsequent reads are pure memory lookups.
// Writes are debounced so bulk assignments trigger only one serialisation.

let _tierCache = null;
let _componentCache = null;
let _tierSaveTimer = null;
let _componentSaveTimer = null;

const DEBOUNCE_MS = 600;

// ─── Constants ────────────────────────────────────────────────────────────────

export const FAULT_SEVERITIES = {
  CRITICAL: "critical",
  HIGH: "high",
  MODERATE: "moderate",
  HEALTHY: "healthy",
};

export const FAULT_COLORS = {
  critical: "#E24B4A",
  high: "#EF9F27",
  moderate: "#F5C842",
  healthy: "#008D8A",
};

export const FAULT_LIST = [
  // Critical
  {
    id: "fault-001",
    severity: "critical",
    name: "Bearing Wear",
    description: "Severe bearing degradation detected",
    riskLabel: "Failure risk in",
    riskHighlight: "18 hours",
    action: "Schedule shutdown + replace bearing",
  },
  {
    id: "fault-002",
    severity: "critical",
    name: "Seal Failure",
    description: "Critical seal integrity loss",
    riskLabel: "Failure risk in",
    riskHighlight: "24 hours",
    action: "Emergency maintenance required",
  },
  {
    id: "fault-003",
    severity: "critical",
    name: "Motor Overheating",
    description: "Temperature exceeds safe limits",
    riskLabel: "Failure risk in",
    riskHighlight: "12 hours",
    action: "Immediate shutdown recommended",
  },
  {
    id: "fault-004",
    severity: "critical",
    name: "Pressure Anomaly",
    description: "Dangerous pressure fluctuations",
    riskLabel: "Failure risk in",
    riskHighlight: "6 hours",
    action: "Stop operation immediately",
  },
  // High
  {
    id: "fault-005",
    severity: "high",
    name: "Vibration Anomaly",
    description: "Abnormal vibration patterns detected",
    riskLabel: "Misalignment suspected",
    riskHighlight: null,
    action: "Inspect coupling",
  },
  {
    id: "fault-006",
    severity: "high",
    name: "Oil Contamination",
    description: "Lubricant quality degraded",
    riskLabel: "Maintenance required",
    riskHighlight: null,
    action: "Schedule oil change",
  },
  {
    id: "fault-007",
    severity: "high",
    name: "Belt Wear",
    description: "Drive belt showing signs of wear",
    riskLabel: "Replacement needed",
    riskHighlight: null,
    action: "Replace belt during next shutdown",
  },
  {
    id: "fault-008",
    severity: "high",
    name: "Cooling System Issue",
    description: "Reduced cooling efficiency",
    riskLabel: "Performance degraded",
    riskHighlight: null,
    action: "Inspect cooling system",
  },
  {
    id: "fault-009",
    severity: "high",
    name: "Electrical Fault",
    description: "Irregular power consumption",
    riskLabel: "Investigation required",
    riskHighlight: null,
    action: "Check electrical connections",
  },
  // Moderate
  {
    id: "fault-010",
    severity: "moderate",
    name: "Minor Vibration",
    description: "Slight vibration increase",
    riskLabel: "Monitor condition",
    riskHighlight: null,
    action: "Continue monitoring",
  },
  {
    id: "fault-011",
    severity: "moderate",
    name: "Temperature Variance",
    description: "Operating temperature fluctuating",
    riskLabel: "Within acceptable range",
    riskHighlight: null,
    action: "Monitor trends",
  },
  {
    id: "fault-012",
    severity: "moderate",
    name: "Filter Maintenance Due",
    description: "Filter replacement recommended",
    riskLabel: "Scheduled maintenance",
    riskHighlight: null,
    action: "Replace filter next service",
  },
];

// Pre-built lookup so we never re-filter FAULT_LIST at call time
const _faultsBySeverity = FAULT_LIST.reduce((acc, f) => {
  if (!acc[f.severity]) acc[f.severity] = [];
  acc[f.severity].push(f);
  return acc;
}, {});

// ─── Internal cache helpers ───────────────────────────────────────────────────

const _getTierCache = () => {
  if (_tierCache === null) {
    try {
      const raw = localStorage.getItem("equipment_tier_faults");
      _tierCache = raw ? JSON.parse(raw) : {};
    } catch {
      _tierCache = {};
    }
  }
  return _tierCache;
};

const _scheduleTierSave = () => {
  if (_tierSaveTimer) clearTimeout(_tierSaveTimer);
  _tierSaveTimer = setTimeout(() => {
    try {
      localStorage.setItem("equipment_tier_faults", JSON.stringify(_tierCache));
    } catch { }
  }, DEBOUNCE_MS);
};

const _getComponentCache = () => {
  if (_componentCache === null) {
    try {
      const raw = localStorage.getItem("component_faults");
      _componentCache = raw ? JSON.parse(raw) : {};
    } catch {
      _componentCache = {};
    }
  }
  return _componentCache;
};

const _scheduleComponentSave = () => {
  if (_componentSaveTimer) clearTimeout(_componentSaveTimer);
  _componentSaveTimer = setTimeout(() => {
    try {
      localStorage.setItem("component_faults", JSON.stringify(_componentCache));
    } catch { }
  }, DEBOUNCE_MS);
};

// ─── Public fault helpers ─────────────────────────────────────────────────────

export const assignRandomFault = () => {
  const r = Math.random();
  if (r < 0.15) {
    const pool = _faultsBySeverity["critical"];
    return pool[Math.floor(Math.random() * pool.length)];
  }
  if (r < 0.40) {
    const pool = _faultsBySeverity["high"];
    return pool[Math.floor(Math.random() * pool.length)];
  }
  if (r < 0.60) {
    const pool = _faultsBySeverity["moderate"];
    return pool[Math.floor(Math.random() * pool.length)];
  }
  return null; // healthy
};

export const getHighestSeverity = (items) => {
  if (!items || items.length === 0) return "healthy";
  if (items.some((i) => i.fault?.severity === "critical")) return "critical";
  if (items.some((i) => i.fault?.severity === "high")) return "high";
  if (items.some((i) => i.fault?.severity === "moderate")) return "moderate";
  return "healthy";
};

export const calculateHealthScore = (severity) => {
  switch (severity) {
    case "critical": return Math.floor(Math.random() * 20) + 5;   // 5–25
    case "high": return Math.floor(Math.random() * 40) + 35;  // 35–75
    case "moderate": return Math.floor(Math.random() * 15) + 75;  // 75–90
    default: return Math.floor(Math.random() * 10) + 90;  // 90–100
  }
};

// ─── Tier fault management ────────────────────────────────────────────────────

/** Returns the current in-memory tier fault map (no localStorage hit). */
export const getFaultAssignments = () => _getTierCache();

/**
 * Replaces the tier cache and schedules a debounced persist.
 * Used by FaultContext to sync state → cache.
 */
export const saveFaultAssignments = (faultMap) => {
  _tierCache = faultMap;
  _scheduleTierSave();
};

/**
 * Assigns faults to an array of tier objects in one pass.
 * Already-cached tiers are returned instantly; new ones get a random fault.
 * A single debounced write covers the whole batch.
 */
export const assignFaultsToTiers = (tiers) => {
  // ----------------------------------------
  // HARD ARRAY VALIDATION
  // ----------------------------------------

  if (!Array.isArray(tiers)) {
    console.warn(
      "assignFaultsToTiers expected array but got:",
      tiers,
    );

    return [];
  }

  if (tiers.length === 0) {
    return [];
  }

  const cache = _getTierCache();

  let dirty = false;

  const result = tiers.map((tier) => {
    if (!tier || !tier.id) {
      return tier;
    }

    if (
      Object.prototype.hasOwnProperty.call(
        cache,
        tier.id,
      )
    ) {
      return {
        ...tier,
        fault: cache[tier.id],
      };
    }

    const fault = assignRandomFault();

    cache[tier.id] = fault;

    dirty = true;

    return {
      ...tier,
      fault,
    };
  });

  if (dirty) {
    _scheduleTierSave();
  }

  return result;
};
/**
 * Assigns a fault to a single tier (used outside bulk contexts).
 * Returns the fault (may be null for healthy).
 */
export const assignFaultToTier = (tierId) => {
  const cache = _getTierCache();
  if (Object.prototype.hasOwnProperty.call(cache, tierId)) return cache[tierId];
  const fault = assignRandomFault();
  cache[tierId] = fault;
  _scheduleTierSave();
  return fault;
};

export const clearFaultAssignments = () => {
  _tierCache = {};
  if (_tierSaveTimer) clearTimeout(_tierSaveTimer);
  localStorage.removeItem("equipment_tier_faults");
};

// ─── Component fault management ───────────────────────────────────────────────

/** Returns the current in-memory component fault map (no localStorage hit). */
export const getComponentFaultAssignments = () => _getComponentCache();

/**
 * Assigns faults to an array of component objects.
 * parentSeverity guarantees at least one component reflects the parent tier's
 * worst state, but only overrides a newly-assigned component — never one that
 * was already persisted.
 */
export const assignFaultsToComponentsUtil = (components, parentSeverity) => {
  if (!components || components.length === 0) return [];

  const cache = _getComponentCache();
  let dirty = false;
  let hasParentSeverity = false;

  // Track which ids were already cached before this call
  const preExisting = new Set(Object.keys(cache));

  const result = components.map((c) => {
    if (preExisting.has(String(c.id))) {
      if (cache[c.id]?.severity === parentSeverity) hasParentSeverity = true;
      return { ...c, fault: cache[c.id] };
    }
    const fault = assignRandomFault();
    if (fault?.severity === parentSeverity) hasParentSeverity = true;
    cache[c.id] = fault;
    dirty = true;
    return { ...c, fault };
  });

  // Force parent severity onto one newly-assigned component if needed
  if (
    parentSeverity &&
    parentSeverity !== "healthy" &&
    !hasParentSeverity &&
    result.length > 0
  ) {
    const pool = _faultsBySeverity[parentSeverity] ?? [];
    if (pool.length > 0) {
      const forcedFault = pool[Math.floor(Math.random() * pool.length)];

      // Prefer a component that was just assigned (not pre-existing)
      const freshIndices = result
        .map((c, i) => (!preExisting.has(String(c.id)) ? i : -1))
        .filter((i) => i !== -1);

      const targetIdx =
        freshIndices.length > 0
          ? freshIndices[Math.floor(Math.random() * freshIndices.length)]
          : Math.floor(Math.random() * result.length);

      result[targetIdx] = { ...result[targetIdx], fault: forcedFault };
      cache[result[targetIdx].id] = forcedFault;
      dirty = true;
    }
  }

  if (dirty) _scheduleComponentSave();
  return result;
};

export const clearComponentFaultAssignments = () => {
  _componentCache = {};
  if (_componentSaveTimer) clearTimeout(_componentSaveTimer);
  localStorage.removeItem("component_faults");
};

// ─── Nuclear reset (clears everything) ───────────────────────────────────────
export const clearAllFaultAssignments = () => {
  clearFaultAssignments();
  clearComponentFaultAssignments();
};